import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'
import { stickerAPI } from '#/api/sticker'


interface FormInputs {
  id: string
}

interface DeleteStickerModalProps {
  id: string
  onClose: () => void
}

export function DeleteStickerModal({ id, onClose }: DeleteStickerModalProps) {
  const { t } = useTranslation([ 'stickers' ])
  const { organization } = useParams()
  const queryClient = useQueryClient()

  const form = useForm<FormInputs>({
    defaultValues: {
      id: ''
    }
  })

  const removeStickerMutation = useMutation({
    mutationFn: () => stickerAPI.delete(id),
    onSuccess: () => {
      // Invalidate the specific sticker and the sticker list
      queryClient.invalidateQueries({ queryKey: [ 'organizations', organization, 'stickers' ] })
      queryClient.removeQueries({
        queryKey: [ 'organizations', organization, 'stickers', id ]
      })
      toast.success(t('sticker_list.menu.toasts.remove_success'))
      onClose()
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  const handleSubmit = form.handleSubmit(values => {
    if ((values.id ?? '').trim() !== id.trim()) {
      form.setError('id', {
        type: 'manual',
        message: t('sticker_list.menu.toasts.remove_mismatch')
      })

      return
    }

    removeStickerMutation.mutate()
  })

  return (
    <Dialog open modal onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sticker_list.menu.remove_title', { id })}</DialogTitle>
          <DialogDescription>{t('sticker_list.menu.remove_description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {removeStickerMutation.error && (
              <AlertBox
                error
                title={mutationError(removeStickerMutation.error, {
                  default: t('sticker_list.menu.toasts.remove_error')
                })}
              />
            )}

            <FormField
              name="id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('sticker_list.menu.remove_field')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('sticker_list.menu.remove_cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={removeStickerMutation.isPending}
              >
                {t('sticker_list.menu.remove_submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
